import { Button } from "@/components/ui/button"
import { toast } from "@/components/ui/use-toast"
import { UserState } from "src/auth/user.tsx"
import { useState } from "react"
import { CloudArrowUpIcon } from "@heroicons/react/24/outline"
import { useDropzone } from "react-dropzone"
import { classes } from "@/utils"

interface LedgerAccountUploadResponse {
  success_count: number
  duplicate_count: number
  error_count: number
  errors: string[]
}

export function LedgerAccountUploadSection() {
  return (
    <div class="bg-white rounded-lg shadow-sm p-6">
      <h2 class="text-lg font-semibold leading-7 text-gray-900 mb-4">Ledger Account CSV Upload</h2>
      <LedgerAccountUploadComponent />
    </div>
  )
}

function LedgerAccountUploadComponent() {
  const [isUploading, setIsUploading] = useState(false)
  const [uploadResult, setUploadResult] = useState<LedgerAccountUploadResponse | null>(null)

  function handleFileUpload(files: File[]) {
    const file = files[0]
    
    if (!file) return

    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast({
        title: "Invalid File Type",
        description: "Please upload a CSV file",
        variant: "destructive",
      })
      return
    }

    uploadCsvFile(file)
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFileUpload,
    multiple: false,
    accept: {
      'text/csv': ['.csv'],
    },
  })

  async function uploadCsvFile(file: File) {
    setIsUploading(true)
    setUploadResult(null)

    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await fetch('/api/organization/ledger-accounts/upload', {
        method: 'POST',
        credentials: 'same-origin',
        body: formData,
      })

      if (!response.ok) {
        const errorText = await response.text()
        let errorMessage = "Upload failed"
        try {
          const errorData = JSON.parse(errorText)
          errorMessage = errorData.message || errorMessage
        } catch (e) {
          errorMessage = errorText || errorMessage
        }
        
        toast({
          title: "Upload Failed",
          description: errorMessage,
          variant: "destructive",
        })
        return
      }

      const data: LedgerAccountUploadResponse = await response.json()
      setUploadResult(data)
      toast({
        title: "Upload Complete",
        description: `Successfully processed ${data.success_count} accounts, ${data.duplicate_count} duplicates, ${data.error_count} errors`,
      })
    } catch (error) {
      console.error("Error uploading file:", error)
      toast({
        title: "Upload Failed",
        description: "An error occurred while uploading the file",
        variant: "destructive",
      })
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <div class="space-y-4">
      <div
        {...(getRootProps() as any)}
        class={classes(
          "border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer",
          isDragActive ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-gray-400",
          isUploading && "opacity-50 cursor-not-allowed"
        )}
      >
        <input {...getInputProps()} disabled={isUploading} />
        <CloudArrowUpIcon class="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <p class="text-lg font-medium text-gray-900 mb-2">
          {isDragActive ? "Drop CSV file here" : "Drag & drop a CSV file"}
        </p>
        <p class="text-sm text-gray-600">
          or click to select a CSV file with ledger account data.
        </p>
        {isUploading && (
          <div class="mt-4">
            <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
            <p class="text-sm text-gray-600 mt-2">Processing CSV file...</p>
          </div>
        )}
      </div>

      {uploadResult && (
        <div class="bg-gray-50 rounded-lg p-4">
          <h3 class="text-sm font-medium text-gray-900 mb-2">Upload Results</h3>
          <div class="space-y-1 text-sm">
            <div class="flex justify-between">
              <span>Successfully created:</span>
              <span class="text-green-600 font-medium">{uploadResult.success_count}</span>
            </div>
            <div class="flex justify-between">
              <span>Duplicates skipped:</span>
              <span class="text-yellow-600 font-medium">{uploadResult.duplicate_count}</span>
            </div>
            <div class="flex justify-between">
              <span>Errors:</span>
              <span class="text-red-600 font-medium">{uploadResult.error_count}</span>
            </div>
          </div>

          {uploadResult.errors.length > 0 && (
            <div class="mt-3">
              <h4 class="text-sm font-medium text-gray-900 mb-1">Error Details:</h4>
              <div class="bg-red-50 border border-red-200 rounded p-2 max-h-32 overflow-y-auto">
                {uploadResult.errors.map((error, index) => (
                  <div key={index} class="text-xs text-red-700">
                    {error}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div class="text-xs text-gray-500 space-y-1">
        <div class="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 mb-1">
          <p class="font-medium">Expected CSV format:</p>
          <a
            href="https://docs.google.com/spreadsheets/d/15DxvfejzYdswD-1_873LtJP_rZYSNUbMxc_7DZi6bCM/edit?gid=0#gid=0"
            target="_blank"
            rel="noopener noreferrer"
            class="text-blue-600 underline hover:text-blue-800 shrink-0"
          >
            See template here
          </a>
        </div>
        <p>Columns: "Account #", "Description", "Account" (or "Full name")</p>
        <p>Example: 4504, "Discounts", "Gross Revenue : Discounts : Scans / MCB"</p>
      </div>
    </div>
  )
}
